import React, { useEffect, useMemo, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Select,
  Input,
  Button,
  Table,
  Typography,
  Space,
  Tooltip,
  Modal,
  DatePicker,
  message,
  Tabs,
  Form,
  Radio
} from 'antd';
import {
  EditFilled,
  DeleteFilled,
  SearchOutlined,
  PlusOutlined,
  UploadOutlined,
  DownloadOutlined,
  EyeFilled,
  CheckCircleFilled,
  CloseCircleFilled,
  CheckOutlined,
  ExclamationCircleFilled,
  FilterOutlined,
  ClearOutlined
} from '@ant-design/icons';
import BatteryGauge from 'react-battery-gauge';
import { apiResolvedWarning, apiGetAllWarnings } from '@services/ManageDevices/DeviceServices';
import { apiGetFavoriteFilters } from '@services/ManageCustomers/FavoriteFilterServices';
import { showError, showWarning, showSuccess } from '@components/notification';
import InputUploadImage from '@components/Upload/Upload';
import {
  convertLocaltimeToUTC,
  downloadBlobFile,
  formatWarningOccurrenceDate,
  warningTypes
} from '@utils/utils';
import { UploadFileOutlined } from '@mui/icons-material';
import { URL_API } from '@configs/index';
import moment from 'moment';
import { usePermission } from '@contexts/Auth/UsePermission';
import TabPane from 'antd/es/tabs/TabPane';
import dayjs from 'dayjs';
import FilterAdvanceDrawer from '@components/FilterAdvance';

const { Option } = Select;

const { RangePicker } = DatePicker;
const { TextArea } = Input;

export default function List() {
  const [type, setType] = useState('ALL');
  const [warningList, setWarningList] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [flagSearch, setFlagSearch] = useState(false);
  const [search, setSearch] = useState('');
  const [serialNo, setSerialNo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [visibleModalImport, setVisibleModalImport] = useState(false);
  const { warning } = usePermission();
  const [selectedMonth, setSelectedMonth] = useState(
    moment().startOf('month').format('YYYY-MM-DD')
  );
  const [status, setStatus] = useState('0');
  const [dateRange, setDateRange] = useState([null, null]);
  const [rangeDateString, setRangeDateString] = useState([null, null]);
  const [loading, setLoading] = useState(false);
  const [dataFilterAdvance, setDataFilterAdvance] = useState({});
  const [favoriteFilters, setFavoriteFilters] = useState([]);
  const [selectedFavoriteFilter, setSelectedFavoriteFilter] = useState(undefined);
  const [loadingFavoriteFilters, setLoadingFavoriteFilters] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const openDrawer = () => setIsOpen(true);
  const onDrawerClose = () => setIsOpen(false);

  // Resolution form modal states
  const [resolutionModalVisible, setResolutionModalVisible] = useState(false);
  const [resolutionLoading, setResolutionLoading] = useState(false);
  const [currentWarning, setCurrentWarning] = useState(null);
  const [resolutionForm] = Form.useForm();

  const fetchData = async () => {
    try {
      setLoading(true);
      const listCust =
        selectedFavoriteFilter && favoriteFilters.find((f) => f.value === selectedFavoriteFilter)
          ? favoriteFilters.find((f) => f.value === selectedFavoriteFilter).item_ids?.join(',')
          : undefined;
      const res = await apiGetAllWarnings({
        page: currentPage,
        size: pageSize,
        fromDate: dateRange?.length
          ? convertLocaltimeToUTC(dateRange?.[0], '00:00:00.000')
          : undefined,
        toDate: dateRange?.length
          ? convertLocaltimeToUTC(dateRange?.[1], '23:59:59.999')
          : undefined,
        type: type === 'ALL' ? undefined : type,
        status: status === 'all' ? undefined : Number(status),
        keyWord: search?.trim() ?? undefined,
        isWarning: 1,
        listCust,
        period_reference: type === 'MANUAL_METER_READING' ? selectedMonth : undefined,
        ...dataFilterAdvance
      });
      setWarningList(
        res.data?.data?.map((item, index) => ({
          ...item,
          stt: (currentPage - 1) * pageSize + 1 + index
        }))
      );
      setTotalItems(res.data?.meta?.total);
    } catch (err) {
      showError('Something went wrong!');
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleResolved = (record) => {
    setCurrentWarning(record);
    resolutionForm.setFieldsValue({
      status: 1,
      resolved_at: dayjs(),
      resolved_image: null,
      remarks: ''
    });
    setResolutionModalVisible(true);
  };

  const handleResolutionSubmit = async () => {
    try {
      const values = await resolutionForm.validateFields();
      setResolutionLoading(true);

      // Prepare resolved_image - InputUploadImage returns a single file object or null
      const resolvedImage = values.resolved_image;
      const imageUrls = resolvedImage?.url ? [resolvedImage.url] : [];

      const payload = {
        status: values.status,
        resolved_at: values.resolved_at
          ? dayjs(values.resolved_at).format('YYYY-MM-DD HH:mm:ss')
          : null,
        resolved_image: JSON.stringify(imageUrls),
        remarks: values.remarks || ''
      };

      await apiResolvedWarning(currentWarning?.id, payload);
      showSuccess({ message: 'Cập nhật thành công!' });
      setResolutionModalVisible(false);
      resolutionForm.resetFields();
      fetchData();
    } catch (error) {
      console.error('Error resolving warning:', error);
      if (error?.errorFields) {
        showError('Vui lòng điền đầy đủ thông tin bắt buộc');
      } else {
        showError('Không thể cập nhật trạng thái');
      }
    } finally {
      setResolutionLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, flagSearch, status]);

  const fetchFavoriteFilters = async () => {
    try {
      setLoadingFavoriteFilters(true);
      const res = await apiGetFavoriteFilters();
      const customerFilters = (res.data?.data || []).filter((f) => f.filter_type === 'customer');
      setFavoriteFilters(
        customerFilters.map((f) => ({
          label: `${f.filter_name} (${f.item_ids?.length || 0} KH)`,
          value: f.id,
          item_ids: f.item_ids
        }))
      );
    } catch (err) {
      console.error('Error fetching favorite filters:', err);
    } finally {
      setLoadingFavoriteFilters(false);
    }
  };

  useEffect(() => {
    fetchFavoriteFilters();
  }, []);

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const handleChangePageSize = (current, size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const onChangeDateRange = (dates, dateStrings) => {
    setRangeDateString(dateStrings);
    setDateRange(dates);
  };

  const columns = useMemo(
    () => [
      {
        title: 'STT',
        dataIndex: 'stt',
        width: 65,
        fixed: 'left'
      },
      {
        title: 'Trạng thái',
        dataIndex: 'status',
        fixed: 'left',
        render: (text, record) => {
          const statusConfig = {
            0: { icon: CloseCircleFilled, color: '#e11d48', text: 'Chưa xử lý' },
            1: { icon: CheckCircleFilled, color: '#10b981', text: 'Đã xử lý' },
            2: { icon: ExclamationCircleFilled, color: '#f59e0b', text: 'Không xử lý được' }
          };
          const config = statusConfig[record?.status] || statusConfig[0];
          const StatusIcon = config.icon;

          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <StatusIcon style={{ fontSize: '20px', color: config.color }} />
              <Typography.Text
                style={{
                  fontSize: '14px',
                  color: config.color
                }}>
                {config.text}
              </Typography.Text>
            </div>
          );
        },
        width: 130
      },
      {
        title: 'Số Sêri',
        dataIndex: 'serial_no',
        fixed: 'left',
        width: 70
      },
      {
        title: 'DBĐD',
        dataIndex: 'customer_id',
        fixed: 'left',
        width: 40,
        render: (text) => <span style={{ color: '#0284c7' }}>{text}</span>
      },
      {
        title: 'Tên khách hàng',
        dataIndex: 'customer_name',
        fixed: 'left',
        width: 150
      },
      // {
      //   title: 'Khách hàng',
      //   dataIndex: 'customer_name',
      //   fixed: 'left',
      //   render: (text, record) => {
      //     return (
      //       <>
      //         <span style={{ color: '#0284c7' }}>{record?.customer_id}</span> -{' '}
      //         {record?.customer_name}
      //       </>
      //     );
      //   },
      //   width: 200
      // },
      {
        title: 'Địa chỉ',
        dataIndex: 'address',
        width: 200
      },
      {
        title: 'CN',
        dataIndex: 'branch',
        width: 100
      },
      {
        title: 'TQL',
        dataIndex: 'manage_group',
        width: 100
      },
      {
        title: 'ĐHK',
        dataIndex: 'parent_device',
        width: 100
      },
      {
        title: 'Tuyến',
        dataIndex: 'route',
        width: 100
      },

      {
        title: 'Tên cảnh báo',
        dataIndex: 'name',
        fixed: 'left',
        width: 130
      },
      ...(type !== 'MANUAL_METER_READING'
        ? [
            {
              title: 'Ngày xảy ra',
              dataIndex: 'date_check',
              render: (text, record) => {
                return (
                  <>
                    {record?.type !== 'MANUAL_METER_READING'
                      ? formatWarningOccurrenceDate(record?.date_check)
                      : '-'}
                  </>
                );
              },
              width: 100
            }
          ]
        : []),
      {
        title: 'Ngày thông báo',
        dataIndex: 'created_at',
        render: (text, record) => {
          return <>{moment(record?.created_at)?.format('DD/MM/YYYY HH:mm')}</>;
        },
        width: 100
      },
      {
        title: 'Người xử lý',
        dataIndex: 'resolved_by',
        width: 100
      },
      {
        title: 'Thời điểm xử lý',
        dataIndex: 'resolved_at',
        render: (text, record) => (
          <>{record?.resolved_at ? moment(record?.resolved_at).format('DD/MM/YYYY HH:mm') : '-'}</>
        ),
        width: 120
      },
      {
        title: 'Ảnh xử lý',
        dataIndex: 'resolved_image',
        render: (text, record) => {
          // Parse resolved_image if it's a JSON string
          let images = record?.resolved_image;

          // If it's a string, try to parse it
          if (typeof images === 'string' && images.trim().startsWith('[')) {
            try {
              images = JSON.parse(images);
            } catch (e) {
              console.error('Error parsing resolved_image:', e);
              images = null;
            }
          }

          // Check if we have valid images array
          if (images && Array.isArray(images) && images.length > 0) {
            // If only 1 image, open directly in new tab
            if (images.length === 1) {
              return (
                <Button type="link" onClick={() => window.open(images[0], '_blank')}>
                  Xem ảnh
                </Button>
              );
            }

            // If multiple images, show modal with clickable images
            return (
              <Button
                type="link"
                onClick={() => {
                  Modal.info({
                    title: 'Ảnh xử lý (Click vào ảnh để mở tab mới)',
                    width: 600,
                    content: (
                      <div style={{ marginTop: 16 }}>
                        {images.map((url, index) => (
                          <img
                            key={index}
                            src={url}
                            alt={`Ảnh xử lý ${index + 1}`}
                            onClick={() => window.open(url, '_blank')}
                            style={{
                              width: '100%',
                              marginBottom: 8,
                              borderRadius: 8,
                              cursor: 'pointer',
                              transition: 'transform 0.2s'
                            }}
                            onMouseEnter={(e) => (e.target.style.transform = 'scale(1.02)')}
                            onMouseLeave={(e) => (e.target.style.transform = 'scale(1)')}
                          />
                        ))}
                      </div>
                    )
                  });
                }}>
                Xem ảnh ({images.length})
              </Button>
            );
          }
          return '-';
        },
        width: 100
      },
      {
        title: 'Ghi chú xử lý',
        dataIndex: 'remarks',
        render: (text, record) => <>{record?.remarks || '-'}</>,
        width: 150
      },
      {
        title: 'Ghi chú',
        dataIndex: 'note',
        render: (text, record) => {
          return <>{record?.note}</>;
        },
        width: 200
      },
      {
        title: 'Số ngày tồn tại',
        dataIndex: 'frequency',
        key: 'frequency',
        render: (text, record) => <>{record?.frequency ?? 1}</>,
        width: 200
      },
      ...(type === 'MAGNETIC'
        ? [
            {
              title: 'Số lần xảy ra',
              dataIndex: 'occurrence_count',
              key: 'occurrence_count',
              render: (text, record) => <>{record?.occurrence_count ?? 0}</>,
              width: 130
            }
          ]
        : []),
      {
        title: 'Kỳ tham chiếu',
        dataIndex: 'period_reference',
        key: 'period_reference',
        render: (text, record) => (
          <>{record?.period_reference ? moment(record?.period_reference).format('MM/YYYY') : '-'}</>
        ),
        width: 100
      },
      {
        title: 'Hành động',
        dataIndex: 'actions',
        width: 100,
        fixed: 'right',
        render: (_, record) => (
          <Space size="middle">
            {warning.updateStatus && record?.status == 0 && (
              <>
                <Button
                  onClick={() => handleResolved(record)}
                  icon={<CheckOutlined />}
                  style={{
                    backgroundColor: '#f1f5f9',
                    color: '#0284c7',
                    border: 'none',
                    marginRight: '5px'
                  }}>
                  Cập nhật trạng thái
                </Button>
              </>
            )}
          </Space>
        )
      }
    ],
    [type]
  );

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setFlagSearch(!flagSearch);
  };
  const generateMonthOptions = () => {
    const months = [];
    const current = moment();
    const startOfYear = current.clone().startOf('year');

    for (let m = 0; m <= current.month() + 1; m++) {
      const monthMoment = startOfYear.clone().add(m, 'months');
      months.push({
        label: `Tháng cước ${monthMoment.format('M')}`,
        value: monthMoment.format('YYYY-MM-DD')
      });
    }
    return months;
  };

  const handleMonthChange = (value) => {
    setSelectedMonth(value);
  };

  const handleExportExcelButtonClick = async () => {
    try {
      setLoading(true);
      const listCust =
        selectedFavoriteFilter && favoriteFilters.find((f) => f.value === selectedFavoriteFilter)
          ? favoriteFilters.find((f) => f.value === selectedFavoriteFilter).item_ids?.join(',')
          : undefined;
      await downloadBlobFile(
        `${URL_API}/api/device/warning/export-report`,
        `Danh sách cảnh báo.xlsx`,
        {
          fromDate: dateRange?.length
            ? convertLocaltimeToUTC(dateRange?.[0], '00:00:00.000')
            : undefined,
          toDate: dateRange?.length
            ? convertLocaltimeToUTC(dateRange?.[1], '23:59:59.999')
            : undefined,
          type: type === 'ALL' ? undefined : type,
          status: status === 'all' ? undefined : Number(status),
          keyWord: search?.trim() ?? undefined,
          isWarning: 1,
          listCust,
          ...dataFilterAdvance
        },
        'GET'
      );
    } catch (e) {
      showError('Có lỗi xảy ra, vui lòng liên hệ ban quản trị');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard">
      <Row justify="space-between" align="middle">
        <Col>
          <Typography.Title level={4}>Thống kê cảnh báo</Typography.Title>
        </Col>
        <Col>
          <Space>
            {warning.export && (
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExportExcelButtonClick}
                loading={loading}
                style={{
                  color: '#fff',
                  height: '36px',
                  backgroundColor: '#0284c7',
                  borderColor: '#0284c7',
                  fontSize: '18px'
                }}>
                Xuất file excel
              </Button>
            )}
          </Space>
        </Col>
      </Row>
      <Card bodyStyle={{ padding: '8px' }}>
        <Row gutter={[8, 8]}>
          <Col flex="36px">
            <Button
              icon={<FilterOutlined />}
              onClick={openDrawer}
              style={{
                backgroundColor: 'var(--surface-3)',
                color: 'var(--text)',
                borderColor: 'var(--border)',
                height: '36px',
                width: '36px'
              }}
            />
          </Col>
          <Col lg={4} md={6} sm={12} xs={12}>
            <Select
              placeholder="Chọn Bộ lọc theo dõi"
              value={selectedFavoriteFilter}
              onChange={setSelectedFavoriteFilter}
              style={{ width: '100%', height: '36px' }}
              allowClear
              loading={loadingFavoriteFilters}
              options={favoriteFilters}
            />
          </Col>
          <Col lg={4} md={6} sm={12} xs={12}>
            <Select
              value={type}
              onChange={(v) => {
                setType(v);
                setCurrentPage(1);
              }}
              style={{ width: '100%', height: '36px' }}>
              {warningTypes.map((warning) => (
                <Option key={warning.value} value={warning.value}>
                  {warning.label}
                </Option>
              ))}
            </Select>
          </Col>
          {type === 'MANUAL_METER_READING' && (
            <Col lg={4} md={6} sm={12} xs={12}>
              <Select
                style={{ width: '100%', height: '36px' }}
                placeholder="Chọn kỳ"
                value={selectedMonth}
                onChange={handleMonthChange}>
                {generateMonthOptions().map((month) => (
                  <Option key={month.value} value={month.value}>
                    {month.label}
                  </Option>
                ))}
              </Select>
            </Col>
          )}
          <Col lg={4} md={6} sm={12} xs={12}>
            <RangePicker
              value={dateRange}
              onChange={onChangeDateRange}
              style={{ width: '100%', height: '36px', fontSize: '18px' }}
              format="YYYY-MM-DD"
              placeholder={['Từ ngày', 'Đến ngày']}
            />
          </Col>
          <Col flex="auto" style={{ minWidth: 180 }}>
            <Input
              value={search}
              onChange={handleSearchChange}
              onPressEnter={handleSearch}
              placeholder="Tìm kiếm tên, mã kh..."
              style={{ width: '100%', height: '36px' }}
            />
          </Col>
          <Col flex="none" style={{ maxWidth: '100%' }}>
            <Space wrap style={{ maxWidth: '100%' }}>
              <Button
                icon={<ClearOutlined />}
                onClick={() => {
                  setSearch('');
                  setDateRange([null, null]);
                  setDataFilterAdvance({});
                  setSelectedFavoriteFilter(undefined);
                  setType('ALL');
                  setCurrentPage(1);
                  setFlagSearch((f) => !f);
                }}
                style={{
                  backgroundColor: 'var(--surface-3)',
                  borderColor: 'var(--border)',
                  color: 'var(--text)',
                  height: '36px'
                }}>
                Xoá bộ lọc
              </Button>
              <Button
                onClick={handleSearch}
                icon={<SearchOutlined />}
                style={{
                  backgroundColor: '#0284c7',
                  borderColor: '#0284c7',
                  color: '#fff',
                  height: '36px'
                }}>
                Tìm kiếm
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>
      <Card bodyStyle={{ padding: '0px' }}>
        <Tabs activeKey={status} defaultActiveKey={'0'} onChange={(value) => setStatus(value)}>
          <TabPane tab={`Tất cả`} key={'all'}>
            <Table
              bordered
              loading={loading}
              dataSource={warningList || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                onChange: handleChangePage,
                onShowSizeChange: handleChangePageSize,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
              }}
              style={{
                borderRadius: 8,
                thead: { backgroundColor: '#e2e8f0', color: '#64748b' },
                fontSize: '30px'
              }}
              scroll={{ x: 'max-content', y: 600 }}
            />
          </TabPane>
          <TabPane tab={`Chưa xử lý`} key={'0'}>
            <Table
              bordered
              // size="middle"
              loading={loading}
              dataSource={warningList || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                onChange: handleChangePage,
                onShowSizeChange: handleChangePageSize,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
              }}
              style={{
                borderRadius: 8,
                thead: { backgroundColor: '#e2e8f0', color: '#64748b' },
                fontSize: '30px'
              }}
              scroll={{ x: 'max-content', y: 600 }}
            />
          </TabPane>
          <TabPane tab={`Đã xử lý`} key={'1'}>
            <Table
              bordered
              // size="middle"
              loading={loading}
              dataSource={warningList || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                onChange: handleChangePage,
                onShowSizeChange: handleChangePageSize,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
              }}
              style={{
                borderRadius: 8,
                thead: { backgroundColor: '#e2e8f0', color: '#64748b' },
                fontSize: '30px'
              }}
              scroll={{ x: 'max-content', y: 600 }}
            />
          </TabPane>
          <TabPane tab={`Không xử lý được`} key={'2'}>
            <Table
              bordered
              loading={loading}
              dataSource={warningList || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                onChange: handleChangePage,
                onShowSizeChange: handleChangePageSize,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
              }}
              style={{
                borderRadius: 8,
                thead: { backgroundColor: '#e2e8f0', color: '#64748b' },
                fontSize: '30px'
              }}
              scroll={{ x: 'max-content', y: 600 }}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* Resolution Form Modal */}
      <Modal
        title="Cập nhật trạng thái xử lý cảnh báo"
        open={resolutionModalVisible}
        onCancel={() => {
          setResolutionModalVisible(false);
          resolutionForm.resetFields();
        }}
        onOk={handleResolutionSubmit}
        confirmLoading={resolutionLoading}
        okText="Cập nhật"
        cancelText="Hủy"
        width={600}>
        <Form form={resolutionForm} layout="vertical" style={{ marginTop: 20 }}>
          <Form.Item
            name="status"
            label="Trạng thái"
            rules={[{ required: true, message: 'Vui lòng chọn trạng thái' }]}>
            <Radio.Group>
              <Radio value={1}>Đã xử lý</Radio>
              <Radio value={2}>Không xử lý được</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="resolved_at"
            label="Thời điểm xử lý"
            rules={[{ required: true, message: 'Vui lòng chọn thời điểm xử lý' }]}>
            <DatePicker
              style={{ width: '100%' }}
              showTime={{ format: 'HH:mm:ss' }}
              format="YYYY-MM-DD HH:mm:ss"
              placeholder="Chọn thời điểm"
            />
          </Form.Item>

          <Form.Item name="resolved_image" label="Ảnh xử lý">
            <InputUploadImage
              onChange={(file) => {
                resolutionForm.setFieldsValue({ resolved_image: file });
              }}
            />
          </Form.Item>

          <Form.Item name="remarks" label="Ghi chú">
            <TextArea
              rows={4}
              placeholder="Nhập ghi chú về việc xử lý cảnh báo"
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
      <FilterAdvanceDrawer
        isOpen={isOpen}
        onDrawerClose={onDrawerClose}
        dataFilterAdvance={dataFilterAdvance}
        setDataFilterAdvance={setDataFilterAdvance}
        flagSearch={flagSearch}
        setFlagSearch={setFlagSearch}
        setCurrentPage={setCurrentPage}
      />
    </div>
  );
}
